import { BUILDING_DEFS, COLS, MAP_H, MAP_W, MAX_DEPTH, inBounds } from './constants';
import { RNG_FORK_LABELS, createForks, type RngForkLabel } from './rng';
import { Simulation } from './sim';
import { rebuildRailFlags } from './systems/rail';
import type {
  Building,
  DigOrder,
  Economy,
  FamilyRequest,
  GoodKind,
  GoodsPile,
  Household,
  IncidentsState,
  LettersState,
  Meta,
  OrePile,
  Peep,
  PeepTask,
  PriceState,
  SimState,
  TownState,
  WaterState,
  Workforce,
} from './types';
import { GOOD_KINDS } from './types';
import { BALANCE } from './constants';
import { createTerrain } from './world/terrain';

export const SAVE_KEY = 'boomtown:save:v1';
export const BACKUP_KEY = 'boomtown:save:v1:backup';

export interface SaveV1 {
  v: 1;
  savedAt: number;
  meta: Meta;
  rng: Record<RngForkLabel, number>;
  terrain: { depth: string; oreType: string; oreAmount: string; flags: string; reclaimed?: string; version: number };
  orders: DigOrder[];
  piles: OrePile[];
  /** Absent in pre-industry saves. */
  goodsPiles?: GoodsPile[];
  buildings: Building[];
  peeps: Peep[];
  workforce: Workforce;
  economy: Economy;
  water: WaterState;
  letters: LettersState;
  incidents: IncidentsState;
  jobTargets: { miners: number; haulers: number };
  firstSaleDone: boolean;
  /** Absent in pre-industry saves. */
  firstGoodsSaleDone?: boolean;
  jobsTerrainVersion: number;
  households: Household[];
  familyRequests: FamilyRequest[];
  firstVeteranSeen: boolean;
  kidsGrownUp: number;
  town: TownState;
}

function bytesToBase64(bytes: Uint8Array): string {
  let binary = '';
  const chunk = 0x8000;
  for (let index = 0; index < bytes.length; index += chunk) {
    binary += String.fromCharCode(...bytes.subarray(index, index + chunk));
  }
  return btoa(binary);
}

function base64ToBytes(base64: string, expectedLength: number): Uint8Array {
  const binary = atob(base64);
  if (binary.length !== expectedLength) throw new Error('save: wrong array length');
  const bytes = new Uint8Array(expectedLength);
  for (let index = 0; index < binary.length; index += 1) bytes[index] = binary.charCodeAt(index);
  return bytes;
}

export function serializeSim(sim: Simulation, savedAt: number): SaveV1 {
  const state = sim.state;
  const rng = {} as Record<RngForkLabel, number>;
  for (const label of RNG_FORK_LABELS) rng[label] = sim.forks[label].getState();
  return {
    v: 1,
    savedAt,
    meta: { ...state.meta },
    rng,
    terrain: {
      depth: bytesToBase64(state.terrain.depth),
      oreType: bytesToBase64(state.terrain.oreType),
      oreAmount: bytesToBase64(state.terrain.oreAmount),
      flags: bytesToBase64(state.terrain.flags),
      reclaimed: bytesToBase64(state.terrain.reclaimed),
      version: state.terrain.version,
    },
    orders: [...state.orders.values()].map((order) => ({ ...order })),
    piles: [...state.piles.values()].map((pile) => ({ ...pile })),
    goodsPiles: [...state.goodsPiles.values()].map((pile) => ({ ...pile })),
    buildings: state.buildings.map((building) => ({ ...building, staffAssigned: [...building.staffAssigned] })),
    // pathIdx/pathTerrainVersion are meaningless while path is null; write the
    // canonical form so serialization is a pure function of behavior.
    peeps: state.peeps.map((peep) => ({
      ...peep,
      path: peep.path ? [...peep.path] : null,
      pathIdx: peep.path ? peep.pathIdx : 0,
      pathTerrainVersion: peep.path ? peep.pathTerrainVersion : 0,
      task: { ...peep.task },
    })),
    workforce: {
      ...state.workforce,
      needs: { ...state.workforce.needs },
      unlocked: { ...state.workforce.unlocked },
    },
    economy: {
      ...state.economy,
      market: {
        iron: { ...state.economy.market.iron },
        gold: { ...state.economy.market.gold },
        corn: { ...state.economy.market.corn },
        wheat: { ...state.economy.market.wheat },
        cattle: { ...state.economy.market.cattle },
        glass: { ...state.economy.market.glass },
        chips: { ...state.economy.market.chips },
        power: { ...state.economy.market.power },
      },
      depot: { ...state.economy.depot },
      truck: { ...state.economy.truck },
      goodsDepot: { units: { ...state.economy.goodsDepot.units }, capacityUnits: state.economy.goodsDepot.capacityUnits },
      goodsTruck: { ...state.economy.goodsTruck },
      marketEvent: state.economy.marketEvent ? { ...state.economy.marketEvent } : null,
      ledger: state.economy.ledger.map((entry) => ({ ...entry })),
    },
    water: { ...state.water },
    letters: {
      delivered: state.letters.delivered.map((letter) => ({ ...letter })),
      quota: state.letters.quota ? { ...state.letters.quota } : null,
    },
    incidents: { lastIncidentDay: state.incidents.lastIncidentDay, log: state.incidents.log.map((e) => ({ ...e })) },
    jobTargets: { ...state.jobTargets },
    firstSaleDone: sim.firstSaleDone,
    firstGoodsSaleDone: sim.firstGoodsSaleDone,
    jobsTerrainVersion: sim.jobsTerrainVersion,
    households: state.households.map((household) => ({ ...household, childIds: [...household.childIds] })),
    familyRequests: state.familyRequests.map((request) => ({ ...request })),
    firstVeteranSeen: sim.firstVeteranSeen,
    kidsGrownUp: sim.kidsGrownUp,
    town: {
      ...state.town,
      mineIncomeHistory: [...state.town.mineIncomeHistory],
      townIncomeHistory: [...state.town.townIncomeHistory],
    },
  };
}

const TASK_KINDS = new Set([
  'idle',
  'toDig',
  'digging',
  'toPile',
  'toDepot',
  'toGoodsPile',
  'toOutputPickup',
  'toGoodsDropoff',
  'staffing',
  'offDuty',
]);

const PEEP_JOBS = new Set(['miner', 'hauler', 'staff', 'industry', 'idle']);

/** One commodity's PriceState, defaulted to its mean for pre-industry saves. */
function normalizePrice(stored: PriceState | undefined, meanCents: number): PriceState {
  return {
    price: clampNumber(stored?.price, 1, 10_000_000, meanCents),
    mean: clampNumber(stored?.mean, 1, 10_000_000, meanCents),
    spikeWeeksLeft: clampNumber(stored?.spikeWeeksLeft, 0, 100, 0),
  };
}

function clampNumber(value: unknown, min: number, max: number, fallback: number): number {
  if (typeof value !== 'number' || !Number.isFinite(value)) return fallback;
  return Math.max(min, Math.min(max, value));
}

function normalizeTask(task: PeepTask): PeepTask {
  if (!task || typeof task !== 'object' || !TASK_KINDS.has(task.kind)) return { kind: 'idle' };
  return task;
}

/**
 * Rebuild a Simulation from a save payload. Throws on structural problems —
 * callers treat any throw as "no usable save" and start fresh.
 */
export function deserializeSim(payload: SaveV1): Simulation {
  if (!payload || payload.v !== 1) throw new Error('save: unsupported version');

  const terrain = createTerrain();
  terrain.depth = base64ToBytes(payload.terrain.depth, COLS);
  terrain.oreType = base64ToBytes(payload.terrain.oreType, COLS * MAX_DEPTH);
  terrain.oreAmount = base64ToBytes(payload.terrain.oreAmount, COLS * MAX_DEPTH);
  terrain.flags = base64ToBytes(payload.terrain.flags, COLS);
  // Pre-industry saves have no reclaimed land; createTerrain zeroed the array.
  if (payload.terrain.reclaimed) terrain.reclaimed = base64ToBytes(payload.terrain.reclaimed, COLS);
  terrain.version = clampNumber(payload.terrain.version, 1, Number.MAX_SAFE_INTEGER, 1);
  for (let col = 0; col < COLS; col += 1) {
    if ((terrain.depth[col] ?? 0) > MAX_DEPTH) terrain.depth[col] = MAX_DEPTH;
  }

  const orders = new Map<number, DigOrder>();
  for (const order of payload.orders ?? []) {
    if (!Number.isInteger(order.col) || order.col < 0 || order.col >= COLS) continue;
    orders.set(order.col, {
      col: order.col,
      targetDepth: clampNumber(order.targetDepth, 1, MAX_DEPTH, 1),
      claimedBy: typeof order.claimedBy === 'number' ? order.claimedBy : null,
      suspended: Boolean(order.suspended),
    });
  }

  const piles = new Map<number, OrePile>();
  for (const pile of payload.piles ?? []) {
    if (!Number.isInteger(pile.col) || pile.col < 0 || pile.col >= COLS) continue;
    piles.set(pile.col, {
      col: pile.col,
      ironDt: clampNumber(pile.ironDt, 0, 1_000_000, 0),
      goldDt: clampNumber(pile.goldDt, 0, 1_000_000, 0),
      claimedDt: clampNumber(pile.claimedDt, 0, 1_000_000, 0),
    });
  }

  const goodsPiles = new Map<number, GoodsPile>();
  for (const pile of payload.goodsPiles ?? []) {
    if (!Number.isInteger(pile.col) || pile.col < 0 || pile.col >= COLS) continue;
    if (!GOOD_KINDS.includes(pile.good)) continue;
    goodsPiles.set(pile.col, {
      col: pile.col,
      good: pile.good,
      units: clampNumber(pile.units, 0, 1_000_000, 0),
      claimedUnits: clampNumber(pile.claimedUnits, 0, 1_000_000, 0),
      // Absent in pre-suspension saves; re-proven on the next assign pass.
      suspended: Boolean(pile.suspended),
    });
  }

  const buildings: Building[] = [];
  for (const building of payload.buildings ?? []) {
    if (!(building.type in BUILDING_DEFS)) continue;
    if (!inBounds(building.x, building.y)) continue;
    buildings.push({
      ...building,
      staffAssigned: Array.isArray(building.staffAssigned) ? building.staffAssigned.filter(Number.isInteger) : [],
      // Absent in pre-industry saves.
      inputUnits: clampNumber(building.inputUnits, 0, 10_000, 0),
      // Absent in pre-output-store saves.
      outputUnits: clampNumber(building.outputUnits, 0, 10_000, 0),
      outputClaimedUnits: clampNumber(building.outputClaimedUnits, 0, 1_000, 0),
      outputSuspended: Boolean(building.outputSuspended),
      outageUntilDay: typeof building.outageUntilDay === 'number' ? building.outageUntilDay : null,
    });
  }

  const peeps: Peep[] = [];
  for (const peep of payload.peeps ?? []) {
    if (typeof peep.id !== 'number') continue;
    const path = Array.isArray(peep.path)
      ? peep.path.filter((col) => Number.isInteger(col) && col >= 0 && col < COLS)
      : null;
    peeps.push({
      ...peep,
      job: PEEP_JOBS.has(peep.job) ? peep.job : 'idle',
      role: peep.role === 'spouse' || peep.role === 'child' ? peep.role : 'worker',
      tier: (clampNumber(peep.tier, 0, 3, 0) | 0) as Peep['tier'],
      tenureWeeks: clampNumber(peep.tenureWeeks, 0, 10_000, 0),
      householdId: typeof peep.householdId === 'number' ? peep.householdId : null,
      growsUpWeek: typeof peep.growsUpWeek === 'number' ? peep.growsUpWeek : null,
      familyAskedWeek: typeof peep.familyAskedWeek === 'number' ? peep.familyAskedWeek : null,
      x: clampNumber(peep.x, 0, MAP_W, 1),
      y: clampNumber(peep.y, 0, MAP_H, 1),
      px: clampNumber(peep.px, 0, MAP_W, 1),
      py: clampNumber(peep.py, 0, MAP_H, 1),
      path: path && path.length > 0 ? path : null,
      pathIdx: clampNumber(peep.pathIdx, 0, path?.length ?? 0, 0),
      pathTerrainVersion: clampNumber(peep.pathTerrainVersion, 0, Number.MAX_SAFE_INTEGER, 0),
      repathFailures: clampNumber(peep.repathFailures, 0, 3, 0),
      task: normalizeTask(peep.task),
      carryDt: clampNumber(peep.carryDt, 0, 1_000, 0),
      carryGood: GOOD_KINDS.includes(peep.carryGood as GoodKind) ? peep.carryGood : null,
    });
  }

  const peepIds = new Set(peeps.map((peep) => peep.id));
  const households: Household[] = [];
  for (const household of payload.households ?? []) {
    if (typeof household?.id !== 'number') continue;
    const workerId = typeof household.workerId === 'number' ? household.workerId : null;
    if (workerId !== null && !peepIds.has(workerId)) continue;
    households.push({
      ...household,
      workerId,
      secondAdultId: typeof household.secondAdultId === 'number' ? household.secondAdultId : null,
      childIds: Array.isArray(household.childIds) ? household.childIds.filter((id) => peepIds.has(id)) : [],
      happiness: clampNumber(household.happiness, 0, 1, 0.7),
      dust: clampNumber(household.dust, 0, 1, 0),
    });
  }
  const familyRequests: FamilyRequest[] = [];
  for (const request of payload.familyRequests ?? []) {
    if (typeof request?.id !== 'number') continue;
    familyRequests.push({ ...request });
  }

  const workforce: Workforce = {
    ...payload.workforce,
    morale: clampNumber(payload.workforce?.morale, 0, 1, 0.7),
    familyRequestsSent: clampNumber(payload.workforce?.familyRequestsSent, 0, 1_000, 0),
    familyNeeds: {
      education: clampNumber(payload.workforce?.familyNeeds?.education, 0, 1, 1),
      health: clampNumber(payload.workforce?.familyNeeds?.health, 0, 1, 1),
      community: clampNumber(payload.workforce?.familyNeeds?.community, 0, 1, 1),
      air: clampNumber(payload.workforce?.familyNeeds?.air, 0, 1, 1),
    },
    needs: {
      housing: clampNumber(payload.workforce?.needs?.housing, 0, 1, 1),
      food: clampNumber(payload.workforce?.needs?.food, 0, 1, 1),
      water: clampNumber(payload.workforce?.needs?.water, 0, 1, 1),
      drink: clampNumber(payload.workforce?.needs?.drink, 0, 1, 1),
      gambling: clampNumber(payload.workforce?.needs?.gambling, 0, 1, 1),
    },
    unlocked: {
      drink: Boolean(payload.workforce?.unlocked?.drink),
      gambling: Boolean(payload.workforce?.unlocked?.gambling),
    },
  };

  const storedMarket = payload.economy?.market;
  const storedEvent = payload.economy?.marketEvent;
  const goodsUnits = {} as Record<GoodKind, number>;
  for (const good of GOOD_KINDS) {
    goodsUnits[good] = clampNumber(payload.economy?.goodsDepot?.units?.[good], 0, 1_000_000, 0);
  }
  const economy: Economy = {
    ...payload.economy,
    cashCents: clampNumber(payload.economy?.cashCents, -100_000_000, 1_000_000_000, 0),
    debtCents: clampNumber(payload.economy?.debtCents, 0, 1_000_000_000, 0),
    // Pre-industry saves know only iron and gold; the rest start at mean.
    market: {
      iron: normalizePrice(storedMarket?.iron, BALANCE.ironPriceMeanCents),
      gold: normalizePrice(storedMarket?.gold, BALANCE.goldPriceMeanCents),
      corn: normalizePrice(storedMarket?.corn, BALANCE.commodityMarket.corn.meanCents),
      wheat: normalizePrice(storedMarket?.wheat, BALANCE.commodityMarket.wheat.meanCents),
      cattle: normalizePrice(storedMarket?.cattle, BALANCE.commodityMarket.cattle.meanCents),
      glass: normalizePrice(storedMarket?.glass, BALANCE.commodityMarket.glass.meanCents),
      chips: normalizePrice(storedMarket?.chips, BALANCE.commodityMarket.chips.meanCents),
      power: normalizePrice(storedMarket?.power, BALANCE.commodityMarket.power.meanCents),
    },
    goodsDepot: {
      units: goodsUnits,
      // Recomputed from functional warehouses on the next daily tick.
      capacityUnits: clampNumber(payload.economy?.goodsDepot?.capacityUnits, 0, 1_000_000, 0),
    },
    goodsTruck: {
      state: payload.economy?.goodsTruck?.state === 'selling' ? 'selling' : 'idle',
      returnsOnDay: clampNumber(payload.economy?.goodsTruck?.returnsOnDay, 0, Number.MAX_SAFE_INTEGER, 0),
      cargoValueCents: clampNumber(payload.economy?.goodsTruck?.cargoValueCents, 0, 1_000_000_000, 0),
    },
    marketEvent:
      storedEvent && (storedEvent.kind === 'boom' || storedEvent.kind === 'bust')
        ? {
            good: storedEvent.good,
            kind: storedEvent.kind,
            weeksLeft: clampNumber(storedEvent.weeksLeft, 0, 100, 0),
          }
        : null,
    ledger: Array.isArray(payload.economy?.ledger) ? payload.economy.ledger.slice(-200) : [],
    // Absent in pre-severance saves — must default or dismissal NaNs the till.
    weekSeveranceCents: clampNumber(payload.economy?.weekSeveranceCents, 0, 1_000_000_000, 0),
  };

  const ERAS = ['company', 'pullout', 'installments', 'independent'];
  const state: SimState = {
    meta: {
      seed: clampNumber(payload.meta?.seed, 0, 0xffffffff, 1) >>> 0,
      tick: clampNumber(payload.meta?.tick, 0, Number.MAX_SAFE_INTEGER, 0),
      speed: [0, 1, 3, 8].includes(payload.meta?.speed) ? payload.meta.speed : 1,
      gameOver: Boolean(payload.meta?.gameOver),
      gameOverReason: payload.meta?.gameOverReason ?? null,
      nextEntityId: clampNumber(payload.meta?.nextEntityId, 1, Number.MAX_SAFE_INTEGER, 1),
      townName: typeof payload.meta?.townName === 'string' ? payload.meta.townName.slice(0, 24) : null,
      era: ERAS.includes(payload.meta?.era) ? payload.meta.era : 'company',
      pulloutWeek: typeof payload.meta?.pulloutWeek === 'number' ? payload.meta.pulloutWeek : null,
      installments: payload.meta?.installments
        ? {
            totalCents: clampNumber(payload.meta.installments.totalCents, 0, 1e9, 0),
            weeklyCents: clampNumber(payload.meta.installments.weeklyCents, 0, 1e9, 0),
            paidWeeks: clampNumber(payload.meta.installments.paidWeeks, 0, 100, 0),
            totalWeeks: clampNumber(payload.meta.installments.totalWeeks, 1, 100, 10),
            missedWeeks: clampNumber(payload.meta.installments.missedWeeks, 0, 100, 0),
          }
        : null,
      ending: payload.meta?.ending ?? null,
    },
    terrain,
    orders,
    piles,
    goodsPiles,
    buildings,
    peeps,
    households,
    familyRequests,
    workforce,
    economy,
    // Older saves predate supplyRatio; a loaded town starts fully supplied.
    water: { ...payload.water, supplyRatio: clampNumber(payload.water?.supplyRatio, 0, 1, 1) },
    town: {
      attractiveness: clampNumber(payload.town?.attractiveness, 0, 1, 0),
      endureStreak: clampNumber(payload.town?.endureStreak, 0, 1000, 0),
      insolventStreak: clampNumber(payload.town?.insolventStreak, 0, 1000, 0),
      postOreWeeks: clampNumber(payload.town?.postOreWeeks, 0, 100000, 0),
      oreMinedTotalDt: clampNumber(payload.town?.oreMinedTotalDt, 0, 1e9, 0),
      quotasCompleted: clampNumber(payload.town?.quotasCompleted, 0, 1000, 0),
      mineIncomeHistory: Array.isArray(payload.town?.mineIncomeHistory)
        ? payload.town.mineIncomeHistory.slice(-4)
        : [],
      townIncomeHistory: Array.isArray(payload.town?.townIncomeHistory)
        ? payload.town.townIncomeHistory.slice(-4)
        : [],
      weekMineIncomeCents: clampNumber(payload.town?.weekMineIncomeCents, 0, 1e9, 0),
      weekGoodsIncomeCents: clampNumber(payload.town?.weekGoodsIncomeCents, 0, 1e9, 0),
    },
    letters: payload.letters ?? { delivered: [], quota: null },
    incidents: payload.incidents ?? { lastIncidentDay: -10, log: [] },
    jobTargets: {
      miners: clampNumber(payload.jobTargets?.miners, 0, 500, 4),
      haulers: clampNumber(payload.jobTargets?.haulers, 0, 500, 2),
    },
  };

  const forks = createForks(state.meta.seed);
  for (const label of RNG_FORK_LABELS) {
    const stored = payload.rng?.[label];
    if (typeof stored === 'number') forks[label].setState(stored);
  }

  const sim = new Simulation(state, forks);
  // Saves from before rail rights-of-way existed lack the corridor flags;
  // a no-op for current saves, so round-trip determinism is preserved.
  rebuildRailFlags(state);
  sim.firstSaleDone = Boolean(payload.firstSaleDone);
  sim.firstGoodsSaleDone = Boolean(payload.firstGoodsSaleDone);
  sim.firstVeteranSeen = Boolean(payload.firstVeteranSeen);
  sim.kidsGrownUp = clampNumber(payload.kidsGrownUp, 0, 100_000, 0);
  sim.networksDirty = true;
  sim.jobsTerrainVersion =
    typeof payload.jobsTerrainVersion === 'number' ? payload.jobsTerrainVersion : terrain.version;
  return sim;
}

// ---- browser storage (every access try/catch; failure degrades silently) ----

export function saveGame(sim: Simulation): boolean {
  try {
    const payload = JSON.stringify(serializeSim(sim, Date.now()));
    const storage = globalThis.localStorage;
    const previous = storage.getItem(SAVE_KEY);
    if (previous !== null) storage.setItem(BACKUP_KEY, previous);
    storage.setItem(SAVE_KEY, payload);
    return true;
  } catch {
    return false;
  }
}

export function loadGame(): Simulation | null {
  for (const key of [SAVE_KEY, BACKUP_KEY]) {
    try {
      const raw = globalThis.localStorage.getItem(key);
      if (raw === null) continue;
      return deserializeSim(JSON.parse(raw) as SaveV1);
    } catch {
      // Fall through to the backup, then to a fresh game.
    }
  }
  return null;
}

/** Whether a resumable game exists, without the cost of deserializing it. */
export function hasSave(): boolean {
  try {
    return (
      globalThis.localStorage.getItem(SAVE_KEY) !== null || globalThis.localStorage.getItem(BACKUP_KEY) !== null
    );
  } catch {
    return false;
  }
}

export function clearSave(): void {
  try {
    globalThis.localStorage.removeItem(SAVE_KEY);
    globalThis.localStorage.removeItem(BACKUP_KEY);
  } catch {
    // Nothing to clear.
  }
}

// ---- best legacy (never cleared with the save) ----

const LEGACY_KEY = 'boomtown:legacy:best';

export interface BestLegacy {
  total: number;
  title: string;
  townName: string;
  week: number;
}

// QA sessions (?qa) must leave every persistent record untouched — including
// the territorial best — so scripted runs can't pollute a player's history.
let persistenceDisabled = false;

export function disablePersistence(): void {
  persistenceDisabled = true;
}

export function readBestLegacy(): BestLegacy | null {
  try {
    const raw = globalThis.localStorage.getItem(LEGACY_KEY);
    if (raw === null) return null;
    const parsed = JSON.parse(raw) as BestLegacy;
    if (typeof parsed?.total !== 'number') return null;
    return parsed;
  } catch {
    return null;
  }
}

export function writeBestLegacy(candidate: BestLegacy): boolean {
  try {
    const current = readBestLegacy();
    if (current && current.total >= candidate.total) return false;
    if (persistenceDisabled) return true; // still report "new best" honestly
    globalThis.localStorage.setItem(LEGACY_KEY, JSON.stringify(candidate));
    return true;
  } catch {
    return false;
  }
}
